import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { HERO_SLIDES } from "../mock/mock";
import { useContent } from "../context/ContentContext";

const HeroCarousel = () => {
  const { content } = useContent();
  const slides = (content.hero_slides && content.hero_slides.length ? content.hero_slides : HERO_SLIDES).filter((s) => s.active !== false);
  const [idx, setIdx] = useState(0);
  const n = slides.length || 1;

  useEffect(() => {
    if (n <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % n), 5500);
    return () => clearInterval(t);
  }, [n]);

  useEffect(() => { if (idx >= n) setIdx(0); }, [n, idx]);

  if (slides.length === 0) return null;

  return (
    <section className="relative w-full">
      <div className="relative max-w-7xl mx-auto px-4 md:px-6 lg:px-10 pt-6">
        <div className="relative rounded-3xl overflow-hidden bg-cream-dark min-h-[420px] md:min-h-[500px] lg:min-h-[560px]">
          {slides.map((slide, i) => (
            <div key={slide.id || i} className={`absolute inset-0 transition-opacity duration-700 ${i === idx ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
              <div className="absolute inset-0">
                <img src={slide.image} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#2a1a15]/85 via-[#2a1a15]/55 to-transparent" />
              </div>
              <div className="relative h-full flex items-center">
                <div className="px-6 md:px-12 lg:px-16 py-14 max-w-2xl text-white">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-gold/60 text-gold text-[11px] tracking-[0.25em] font-semibold">✦ {(slide.tag || "").toUpperCase()}</div>
                  <h1 className="mt-4 font-serif-brand text-[38px] md:text-[54px] lg:text-[64px] leading-[1.05] font-bold ardf-fade-up">{slide.title}</h1>
                  <p className="mt-4 text-[15px] md:text-[17px] text-[#f2e7d1] max-w-lg leading-relaxed">{slide.subtitle}</p>
                  <div className="mt-7 flex items-center gap-3">
                    {slide.external ? (
                      <a href={slide.to} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-gold hover:bg-[#b6944f] text-[#2a1a15] font-semibold px-6 py-3 rounded-full transition-colors">{slide.cta} <ArrowRight className="h-4 w-4" /></a>
                    ) : (
                      <Link to={slide.to} className="inline-flex items-center gap-2 bg-gold hover:bg-[#b6944f] text-[#2a1a15] font-semibold px-6 py-3 rounded-full transition-colors">{slide.cta} <ArrowRight className="h-4 w-4" /></Link>
                    )}
                    <Link to="/about" className="inline-flex items-center gap-2 border border-white/60 text-white hover:bg-white hover:text-[#2a1a15] font-medium px-6 py-3 rounded-full transition-colors">Our Legacy</Link>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {slides.length > 1 && (
            <>
              <button onClick={() => setIdx((idx - 1 + n) % n)} aria-label="Prev" className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/85 hover:bg-white text-[#2a1a15] flex items-center justify-center backdrop-blur transition-colors">
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button onClick={() => setIdx((idx + 1) % n)} aria-label="Next" className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/85 hover:bg-white text-[#2a1a15] flex items-center justify-center backdrop-blur transition-colors">
                <ChevronRight className="h-5 w-5" />
              </button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
                {slides.map((_, i) => (
                  <button key={i} onClick={() => setIdx(i)} aria-label={`Slide ${i + 1}`} className={`h-2 rounded-full transition-all ${i === idx ? "w-8 bg-gold" : "w-2 bg-white/60 hover:bg-white"}`} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default HeroCarousel;
